import { Divider, List, Position, Size, Tooltip } from '@busybusy/webapp-react-ui';
import { useQuery } from '@tanstack/react-query';
import { type CostCode } from '__generated__/graphql';
import classNames from 'classnames';
import { ArchivedStatus } from 'components/domain/archived/ArchivedPicker/ArchivedPicker';
import PickerList from 'components/foundation/pickers/PickerList/PickerList';
import PickerListPicker from 'components/foundation/pickers/PickerListPicker/PickerListPicker';
import PickerListSearchHeader from 'components/foundation/pickers/PickerListSearchHeader/PickerListSearchHeader';
import { findOrFetchPickerItem } from 'components/foundation/pickers/utils';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { t } from 'i18next';
import _ from 'lodash';
import * as React from 'react';
import { ReactNode } from 'react';
import { ClassName } from 'types/ClassName';
import { highlight } from 'utils/stringUtils';
import { IconButton } from '../../..';
import { NotCertifiedIcon } from '../../../../assets/icons';
import { projectUtils } from '../../../../utils';
import ProjectPickerDrillHeader from '../ProjectPickerDrillHeader/ProjectPickerDrillHeader';
import ProjectPickerRow from '../ProjectPickerRow/ProjectPickerRow';
import ProjectPickerTitle from '../ProjectPickerTitle/ProjectPickerTitle';
import { useProjectPickerData, type ProjectPickerItem, type ProjectPickerLayer } from './hooks/useProjectPickerData';

export interface IProjectPickerProps {
  value: string | null;
  error?: boolean;
  onSelect: (projectId: string | null) => void;
  placeholder?: string;
  className?: ClassName;
  position?: Position;
  client?: any;
  dropDownButtonRender?: ReactNode;
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode;
  fetchPolicy?: string;
  archivedStatus?: ArchivedStatus;
  blacklistedIds?: string[];
  topLevelOnly?: boolean;
  scopeToCostCodeIds?: Array<CostCode['id']>;
  disabled?: boolean;
  showMissingCertsWarning?: boolean;
}

export const PROJECT_PICKER_SECTION_SIZE = 500;

function ProjectPicker({
  value,
  error,
  onSelect,
  placeholder,
  className,
  position,
  dropDownButtonRender,
  closeButtonRender,
  archivedStatus,
  blacklistedIds,
  topLevelOnly,
  scopeToCostCodeIds,
  disabled,
  showMissingCertsWarning,
}: IProjectPickerProps) {
  const dividerIndex = React.useRef<number | null>(null);
  const pickerRef = React.useRef<HTMLElement | null>(null);

  const baseQueryKey = useReactQueryBaseKey();

  const {
    openPicker,
    closePicker,
    goBack,
    drillInto,
    registerScroller,
    searchValue,
    setSearchValue,
    searchDebounce,
    layers,
    setLayers,
    focusStartId,
    isDrilledIn,
    data,
    isLoading,
    isDataError,
    loadedAll,
    subProjectData: subProjectDataFromQuery,
    isSubLoading,
    isSubError,
    fetchSelected,
  } = useProjectPickerData({
    blacklistedIds,
    archivedStatus,
    topLevelOnly,
    scopeToCostCodeIds,
    pageSize: 10,
    queryKeyPrefix: 'project-picker',
    onProjectsLoaded: (projects) => {
      dividerIndex.current = searchDebounce ? calculateDividerIndex(projects) : null;
    },
  });

  const { data: selectedProject } = useQuery({
    queryKey: [...baseQueryKey, 'project-picker-by-id', value],
    queryFn: () => findOrFetchPickerItem(value!, data, fetchSelected),
    initialData: null,
    enabled: !!value,
  });

  const calculateDividerIndex = (projects: Array<Pick<ProjectPickerItem, 'depth'>>) => {
    const firstSubprojectIndex = projects.findIndex((project) => project.depth !== 1);
    return firstSubprojectIndex ? firstSubprojectIndex - 1 : null;
  };

  const hasActiveSubProjects = (row: Pick<ProjectPickerItem, 'children'>) => {
    return (
      row.children && row.children.length > 0 && !topLevelOnly && row.children.some((item) => item.archivedOn === null)
    );
  };

  const renderBasicRow = (row: ProjectPickerItem, index: number) => {
    const title = <ProjectPickerTitle title={row.title} projectInfo={row.projectInfo} searchValue={searchValue} />;
    return (
      <>
        <ProjectPickerRow
          title={title}
          onChevronClick={hasActiveSubProjects(row) ? (e) => handleChevron(row, e) : undefined}
        />
        {layers.length === 0 && dividerIndex.current === index && <Divider className="my-3" />}
      </>
    );
  };

  const renderSubSearchRow = (project: ProjectPickerItem) => {
    const title = (
      <>
        <div className="picker-row-title ellipsis">
          <ProjectPickerTitle title={project.title} projectInfo={project.projectInfo} searchValue={searchValue} />
        </div>
        <div className="f-sm fc-2 ellipsis">{projectUtils.getFormattedPathFromProject(project)}</div>
      </>
    );
    return (
      <ProjectPickerRow
        key={project.id}
        title={title}
        size={Size.LARGE}
        className="subproject-search-row"
        onChevronClick={hasActiveSubProjects(project) ? (e) => handleChevron(project, e) : undefined}
      />
    );
  };

  function renderSubprojectRow(row: ProjectPickerLayer) {
    return (
      <ProjectPickerRow
        key={row.id}
        title={highlight(row.title, searchValue)}
        className="subproject-row"
        onChevronClick={hasActiveSubProjects(row) ? (e) => handleChevron(row, e) : undefined}
      />
    );
  }

  function renderParentRow(row: ProjectPickerItem, index: number) {
    if (searchValue && row.ancestors && row.ancestors.length && !layers.length) {
      return renderSubSearchRow(row);
    }
    return renderBasicRow(row, index);
  }

  function drillIn(project: ProjectPickerLayer, _e: React.KeyboardEvent | React.MouseEvent) {
    if (project && project.children && project.children.length) {
      drillInto(project);
    }
  }

  function handleGoBack() {
    goBack();
  }

  function handleClear() {
    closePicker();
  }

  function handleOnOpen() {
    openPicker(selectedProject?.id ?? null);
    setLayers(_.sortBy(selectedProject?.ancestors ?? [], 'depth'));
  }

  function onClose() {
    closePicker();
  }

  function handleSelect(project: ProjectPickerItem | null) {
    onSelect(project ? project.id : null);
    setTimeout(() => {
      if (pickerRef.current) pickerRef.current.focus();
    });
  }

  function handleChevron(project: ProjectPickerLayer, e: React.KeyboardEvent | React.MouseEvent) {
    e.stopPropagation();
    drillIn(project, e);
  }

  function renderSecondaryHeader() {
    return <ProjectPickerDrillHeader layers={layers} onBack={handleGoBack} />;
  }

  function renderValueTemplate() {
    if (selectedProject) {
      const label = projectUtils.getFormattedPathFromProject(selectedProject, true);
      if (label) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Tooltip label={label} delay={750}>
              <div className="ellipsis">{label}</div>
            </Tooltip>
            {showMissingCertsWarning && (
              <IconButton
                svg={NotCertifiedIcon}
                tooltipLabel={t(
                  'The required certification to clock time to this project is either missing or expired for one or more selected employees.'
                )}
              />
            )}
          </div>
        );
      }
    }
    return <></>;
  }

  function renderPickerLists(closeMenu: (shouldFocus: boolean) => void) {
    return (
      <>
        {!isDrilledIn ? (
          <PickerList
            key="project-list"
            value={value}
            scrollToId={focusStartId}
            onSelect={(project) => onSelect(project ? project.id : null)}
            closeMenu={closeMenu}
            isLoading={isLoading}
            data={data ?? []}
            error={isDataError}
            loadedAll={loadedAll}
            renderRow={renderParentRow}
            setScrollerRef={registerScroller}
            onRightKeyDown={drillIn}
            onLeftKeyDown={handleGoBack}
            header={(onKeyDown, setFocusIndex) => (
              <PickerListSearchHeader
                className="p-3"
                searchValue={searchValue}
                autofocus={true}
                onSearch={setSearchValue}
                onKeyDown={onKeyDown}
                setFocusIndex={setFocusIndex}
              />
            )}
          >
            {(wrappedRowRender) => <List>{data?.map(wrappedRowRender)}</List>}
          </PickerList>
        ) : (
          <PickerList
            key="subproject-list"
            value={null}
            scrollToId={focusStartId}
            onSelect={(project) => onSelect(project ? project.id : null)}
            closeMenu={closeMenu}
            isLoading={isSubLoading}
            data={subProjectDataFromQuery ?? []}
            error={isSubError}
            loadedAll={loadedAll}
            renderRow={renderSubprojectRow}
            setScrollerRef={registerScroller}
            onRightKeyDown={drillIn}
            onLeftKeyDown={handleGoBack}
            header={renderSecondaryHeader}
            autofocus={true}
          >
            {(wrappedRowRender) => <List>{subProjectDataFromQuery?.map(wrappedRowRender)}</List>}
          </PickerList>
        )}
      </>
    );
  }

  return (
    <PickerListPicker
      value={value}
      onSelect={handleSelect}
      onClear={handleClear}
      className={classNames('project-picker', className)}
      error={error}
      dropDownButtonRender={dropDownButtonRender}
      closeButtonRender={closeButtonRender}
      position={position}
      placeholder={placeholder}
      valueTemplate={renderValueTemplate()}
      onOpen={handleOnOpen}
      onClose={onClose}
      minWidth="350px"
      disabled={disabled}
    >
      {renderPickerLists}
    </PickerListPicker>
  );
}

// TODO: Add archived filtering
// TODO: Implement lazy loading once server-side paging is available

export default ProjectPicker;
